import type React from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate, useParams} from "react-router";
import {Alert, Box, Button, Skeleton, Stack, Switch, Typography} from "@mui/material";
import ArrowIcon from "~/icons/svg/ArrowIcon.svg?react";
import {CustomCard} from "~/components/Card/Card";
import {useStores} from "~/stores";
import {routes} from "~/routing/routes";
import type {DatabagRouteMatch} from "~/routing/catRoutes";
import {COLORS} from "~/style/colors-ts";
import {SUPPLIER_DETAIL_HOME_TAB, SUPPLIER_DETAIL_TABS} from "../constants/supplierDetailTabs";
import {useSupplier360SettingsQuery, useUpdateSupplier360SettingsMutation} from "../hooks/useSupplier360Settings";
import {LOGO_PROVIDER} from "../hooks/useSourceLogoAllowed";
import {SourceLogo} from "~/components/source-logo/SourceLogo";
import {SOURCE_LOGO_LABEL, SOURCE_LOGOS} from "~/components/source-logo/sourceLogos";
import type {SupplierLogoPlace} from "../types/Supplier360Types";

const LOGO_PLACES: {place: SupplierLogoPlace, label: string}[] = [
    {place: 'goods_and_services', label: 'Goods & services'},
    {place: 'insights', label: 'Insights'},
];

/**
 * Supplier 360 settings, for Mithra staff only: which tabs the supplier detail page shows in this environment, and
 * which data providers' logos show in Goods & services and Insights.
 * Applies to every user and every dataset here. The server refuses changes from anyone outside the `mithra_staff`
 * group (as the other staff-only features); the page does not render for them either.
 */
export const SupplierSettingsPage: React.FC = observer(() => {
    const {authStore} = useStores();
    const navigate = useNavigate();
    const {id: databagId} = useParams<DatabagRouteMatch>();
    const {data: settings, isLoading, isError} = useSupplier360SettingsQuery();
    const {mutate: save, isPending} = useUpdateSupplier360SettingsMutation();

    const back = (
        <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<ArrowIcon fontSize="small"/>}
            sx={{alignSelf: 'flex-start'}}
            onClick={() => navigate(generatePath(routes.supplier_360, {id: databagId ?? ''}))}
        >
            Supplier 360
        </Button>
    );

    if (!authStore.isMithraStaffGroup) {
        return <Stack gap={2}>{back}<Alert severity="warning">Supplier 360 settings are for Mithra staff.</Alert></Stack>;
    }

    const hidden = settings?.hidden_tabs ?? [];
    const toggle = (route: string, show: boolean) => save({
        hidden_tabs: show ? hidden.filter(tab => tab !== route) : [...hidden, route],
    });

    return (
        <Stack gap={2} sx={{maxWidth: 720}}>
            {back}
            <Box>
                <Typography component="h1" variant="h5" fontWeight={700}>Supplier 360 settings</Typography>
                <Typography variant="body2" color="text.secondary">
                    Mithra staff only. Applies to every user and every dataset in this environment.
                </Typography>
            </Box>
            {isError && <Alert severity="error">Could not load the settings.</Alert>}
            {isLoading && <Skeleton variant="rounded" height={360}/>}
            {settings && (
                <CustomCard
                    title="Supplier detail tabs"
                    subHeader="Hidden tabs disappear from every supplier's page; links to them open Overview instead."
                >
                    <Stack>
                        {SUPPLIER_DETAIL_TABS.map(({title, Icon, route}) => {
                            const isHome = route === SUPPLIER_DETAIL_HOME_TAB;
                            const shown = !hidden.includes(route);
                            return (
                                <Stack
                                    key={route}
                                    direction="row"
                                    alignItems="center"
                                    gap={1.5}
                                    sx={{py: 1, borderTop: 1, borderColor: COLORS.grey[150], '&:first-of-type': {borderTop: 'none'}}}
                                >
                                    {Icon && <Icon style={{width: 18, height: 18, flexShrink: 0}}/>}
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <Typography variant="body2" fontWeight={500}>{title}</Typography>
                                        {isHome && (
                                            <Typography variant="caption" color="text.secondary">
                                                Always shown: it is the page every supplier opens on.
                                            </Typography>
                                        )}
                                    </Box>
                                    <Switch
                                        checked={shown}
                                        disabled={isHome || isPending}
                                        onChange={(_e, checked) => toggle(route, checked)}
                                        slotProps={{input: {'aria-label': `Show the ${title} tab`}}}
                                    />
                                </Stack>
                            );
                        })}
                    </Stack>
                </CustomCard>
            )}
            {settings && (
                <CustomCard
                    title="Source logos"
                    subHeader="Show a data provider's logo above the values it supplied. Off shows no label at all."
                >
                    <Box sx={{overflowX: 'auto'}}>
                        <Box component="table" sx={{width: '100%', minWidth: 360, borderCollapse: 'collapse',
                            '& th, & td': {py: 1, borderTop: 1, borderColor: COLORS.grey[150]},
                            '& tr:first-of-type > *': {borderTop: 'none'}}}>
                            <thead>
                                <tr>
                                    <Typography component="th" variant="caption" color="text.secondary" sx={{textAlign: 'left'}}>
                                        Provider
                                    </Typography>
                                    {LOGO_PLACES.map(({place, label}) => (
                                        <Typography key={place} component="th" variant="caption" color="text.secondary"
                                                    sx={{width: 140, textAlign: 'center'}}>
                                            {label}
                                        </Typography>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {SOURCE_LOGOS.map(source => (
                                    <tr key={source}>
                                        <td>
                                            <Stack direction="row" alignItems="center" gap={1.5}>
                                                <SourceLogo source={source}/>
                                                <Typography variant="body2" color="text.secondary">{SOURCE_LOGO_LABEL[source]}</Typography>
                                            </Stack>
                                        </td>
                                        {LOGO_PLACES.map(({place, label}) => (
                                            <Box component="td" key={place} sx={{textAlign: 'center'}}>
                                                <Switch
                                                    checked={settings.source_logos[place][LOGO_PROVIDER[source]]}
                                                    disabled={isPending}
                                                    onChange={(_e, checked) => save({
                                                        source_logos: {[place]: {[LOGO_PROVIDER[source]]: checked}},
                                                    })}
                                                    slotProps={{input: {'aria-label': `Show the ${SOURCE_LOGO_LABEL[source]} logo in ${label}`}}}
                                                />
                                            </Box>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                    </Box>
                </CustomCard>
            )}
        </Stack>
    );
});
